import { useEffect, useState } from 'react';
import { createIzziCheckout, fetchDebitNotes, fetchIzziConfig, fetchIzziPayment, refreshIzziPayment } from '../services/api';

const labels: Record<string, string> = { INITIATED: 'En attente de création du checkout', PENDING: 'En attente de paiement', PROCESSING: 'Paiement en cours', CONFIRMED: 'Paiement confirmé', FAILED: 'Paiement échoué', CANCELLED: 'Paiement annulé ou expiré', REFUNDED: 'Paiement remboursé' };
const stored = (key: string) => { try { return sessionStorage.getItem(key) || ''; } catch { return ''; } };
const save = (key: string, value: string) => { try { sessionStorage.setItem(key, value); } catch { /* Server reservations still prevent duplicate checkouts. */ } };

export default function IzziPaymentPanel() {
 const [config, setConfig] = useState<any>(null);
 const [notes, setNotes] = useState<any[]>([]);
 const [declaration, setDeclaration] = useState('');
 const [currency, setCurrency] = useState('USD');
 const [amount, setAmount] = useState('');
 const [payment, setPayment] = useState<any>(null);
 const [paymentID, setPaymentID] = useState(() => stored('fpt-izzi-payment'));
 const [busy, setBusy] = useState(false);
 const [error, setError] = useState('');
 useEffect(() => {
  let active = true;
  Promise.all([fetchIzziConfig(), fetchDebitNotes()]).then(([c, rows]) => {
   if (!active) return;
   setConfig(c);
   const payable = rows.filter((n: any) => ['ISSUED', 'OVERDUE'].includes(n.status));
   setNotes(payable); if (payable.length) setDeclaration(payable[0].declaration_id);
  }).catch(e => { if (active) setError(e.message); });
  return () => { active = false; };
 }, []);
 useEffect(() => {
  if (!paymentID) return;
  let active = true;
  const load = () => fetchIzziPayment(paymentID).then(p => { if (active) setPayment(p); }).catch(e => { if (active) setError(e.message); });
  load(); const timer = setInterval(load, 5000);
  return () => { active = false; clearInterval(timer); };
 }, [paymentID]);
 const start = async () => {
  setBusy(true); setError('');
  const fingerprint = JSON.stringify([declaration, currency, amount]);
  const storageKey = `fpt-izzi-request:${fingerprint}`;
  let key = stored(storageKey); if (!key) { key = crypto.randomUUID(); save(storageKey, key); }
  try {
   const p = await createIzziCheckout({ declaration_id: declaration, currency, amount, idempotency_key: key });
   setPayment(p); setPaymentID(p.payment_id); save('fpt-izzi-payment', p.payment_id);
  } catch (e: any) { setError(e.message); }
  finally { setBusy(false); }
 };
 const refresh = async () => {
  setBusy(true); setError('');
  try { setPayment(await refreshIzziPayment(paymentID)); } catch (e: any) { setError(e.message); }
  finally { setBusy(false); }
 };
 const newAttempt = () => {
  save(`fpt-izzi-request:${JSON.stringify([declaration, currency, amount])}`, '');
  save('fpt-izzi-payment', ''); setPaymentID(''); setPayment(null); setError('');
  fetchDebitNotes().then(rows => setNotes(rows.filter((n: any) => ['ISSUED', 'OVERDUE'].includes(n.status)))).catch(e => setError(e.message));
 };
 return <section className="card" aria-label="Paiement Izzi Pay" style={{ marginBottom: 24 }}>
  <h3>Payer avec Izzi Pay</h3>
  {config?.environment === 'sandbox' && <p className="notice">Environnement de test — aucun règlement réel.</p>}
  {config && !config.enabled && <p className="notice">Izzi Pay n’est pas encore disponible sur cet environnement. Vous pouvez utiliser le virement bancaire ci-dessous.</p>}
  {error && <p className="error-msg" role="alert">{error}</p>}
  {config?.enabled && !payment && <>
   <label>Note de débit<select value={declaration} onChange={e => setDeclaration(e.target.value)} style={{ display: 'block', width: '100%', padding: 8 }}>
    {notes.map(n => <option key={n.id} value={n.declaration_id}>{n.debit_note_reference} — {n.amount_due_usd} USD</option>)}
   </select></label>
   {!notes.length && <p>Aucune note de débit payable.</p>}
   <div className="grid2" style={{ marginTop: 12 }}>
    <label>Devise<select value={currency} onChange={e => setCurrency(e.target.value)}><option>USD</option><option>CDF</option></select></label>
    <label>Montant partiel (facultatif)<input type="number" min="0" step={currency === 'CDF' ? '1' : '0.01'} value={amount} onChange={e => setAmount(e.target.value)} placeholder="Vide = solde restant" /></label>
   </div>
   <p>Le solde et le taux de change seront vérifiés à partir de votre note de débit.</p>
   <button className="btn primary" disabled={busy || !declaration || !notes.length} onClick={start}>{busy ? 'Préparation…' : 'Préparer le paiement Izzi'}</button>
  </>}
  {payment && <div aria-live="polite" style={{ marginTop: 12, lineHeight: 1.5 }}>
   <p><strong>{labels[payment.status] || payment.status}</strong></p>
   <p>{payment.payment_reference} — {payment.amount} {payment.currency}</p>
   {['CREATING', 'UNKNOWN'].includes(payment.state) && <p>La création du checkout est en cours de vérification. Ne créez pas un second paiement.</p>}
   {payment.checkout_url && ['PENDING', 'PROCESSING'].includes(payment.status) && <a className="btn primary" href={payment.checkout_url} target="_blank" rel="noopener noreferrer">Ouvrir le paiement sécurisé Izzi</a>}
   {payment.state === 'READY' && <button className="btn" disabled={busy} onClick={refresh}>Vérifier le paiement</button>}
   {payment.state === 'TERMINAL' && <button className="btn" onClick={newAttempt}>Revenir aux notes de débit</button>}
   <p style={{ marginTop: 12 }}>{payment.status === 'CONFIRMED' ? 'Le paiement est enregistré dans votre suivi FPT.' : 'Le statut est vérifié auprès d’Izzi Pay. Une redirection seule ne confirme pas le règlement.'}</p>
  </div>}
 </section>;
}
